import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export const AboutPage = () => {
  useEffect(() => {
    document.title = 'About — Custom Group AI Advisory';
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f5]">
      {/* Nav */}
      <nav className="bg-[#0a0a0a] border-b border-[#222222] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="text-lg font-semibold tracking-tight text-[#f5f5f5]">
              Custom Group
            </Link>
            <div className="hidden md:flex items-center gap-10 text-sm font-medium text-[#888888]">
              <Link to="/blog" className="hover:text-[#f5f5f5] transition-colors">Insights</Link>
              <Link to="/#approach" className="hover:text-[#f5f5f5] transition-colors">Our Approach</Link>
              <Link to="/#access" className="hover:text-[#f5f5f5] transition-colors">Advisory Access</Link>
              <Link
                to="/apply"
                className="bg-[#E8651A] text-[#0a0a0a] px-5 py-2 text-sm font-semibold hover:bg-[#C9520E] transition-colors"
              >
                Apply for Access
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="border-b border-[#222222]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-24">
          <p className="text-xs text-[#E8651A] uppercase tracking-widest mb-6">About</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-8 max-w-3xl leading-tight">
            Custom Group advises leadership teams on AI that generates measurable economic value.
          </h1>
          <p className="text-[#888888] max-w-2xl leading-relaxed text-lg">
            We are not a software vendor. We are not a technology integrator.
            We are an AI advisory practice — grounded in 13 years of enterprise delivery,
            with one fixed standard: measurable return.
          </p>
        </div>
      </section>

      {/* What We Do */}
      <section className="border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <h2 className="text-2xl font-bold mb-16 tracking-tight">What We Do</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#222222]">
            <div className="bg-[#111111] p-10">
              <p className="text-xs text-[#E8651A] uppercase tracking-widest mb-6">Advisory</p>
              <h3 className="text-base font-bold mb-4">AI Capital Allocation</h3>
              <p className="text-[#888888] text-sm leading-relaxed">
                We advise leadership teams on where in their organisation AI will generate
                measurable economic return — before any capital is committed to technology.
                Strategy precedes deployment. Always.
              </p>
            </div>
            <div className="bg-[#111111] p-10">
              <p className="text-xs text-[#E8651A] uppercase tracking-widest mb-6">Implementation</p>
              <h3 className="text-base font-bold mb-4">Validated Deployment</h3>
              <p className="text-[#888888] text-sm leading-relaxed">
                Where advisory identifies a clear ROI case, we selectively implement.
                Pilot systems, validated use cases, and board-level reporting — built to
                prove or disprove the hypothesis before scale.
              </p>
            </div>
            <div className="bg-[#111111] p-10">
              <p className="text-xs text-[#E8651A] uppercase tracking-widest mb-6">Architecture</p>
              <h3 className="text-base font-bold mb-4">Security-First AI Infrastructure</h3>
              <p className="text-[#888888] text-sm leading-relaxed">
                We design for local deployment and data sovereignty. Your data does not
                leave your infrastructure. We also model API vs. local deployment economics
                — and the cost difference at production volume is consistently significant.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Are Not */}
      <section className="border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <h2 className="text-2xl font-bold mb-8 tracking-tight">What We Are Not</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4 border-l-2 border-[#222222] pl-6">
                <p className="text-[#888888] text-sm leading-relaxed">
                  <span className="text-[#f5f5f5] font-medium">Not a software agency.</span>{' '}
                  We do not build to specification. We advise on where to build, and validate
                  the decision before implementation begins.
                </p>
              </div>
              <div className="flex items-start gap-4 border-l-2 border-[#222222] pl-6">
                <p className="text-[#888888] text-sm leading-relaxed">
                  <span className="text-[#f5f5f5] font-medium">Not a vendor.</span>{' '}
                  We have no technology to sell. Our advisory is economically independent — we
                  recommend what generates return for your organisation, not what generates
                  revenue for us.
                </p>
              </div>
              <div className="flex items-start gap-4 border-l-2 border-[#222222] pl-6">
                <p className="text-[#888888] text-sm leading-relaxed">
                  <span className="text-[#f5f5f5] font-medium">Not a fit for every organisation.</span>{' '}
                  We work with mid-to-large organisations where AI ROI is a board-level priority
                  and leadership has a clear mandate to act. We are not a fit for exploratory
                  or low-budget initiatives.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Firm */}
      <section className="border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <h2 className="text-2xl font-bold mb-16 tracking-tight">The Firm</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <div>
              <p className="text-xs text-[#E8651A] uppercase tracking-widest mb-4">Founded</p>
              <p className="text-[#f5f5f5] font-bold mb-2">2013 — Ballito, KwaZulu-Natal</p>
              <p className="text-[#888888] text-sm leading-relaxed">
                Custom Group has operated for 13 years across property, logistics, education,
                manufacturing, and professional services. The advisory practice was formally
                established in 2024, drawing on a decade of enterprise delivery to answer
                a more important question than "how to build" — "where to invest."
              </p>
            </div>
            <div>
              <p className="text-xs text-[#E8651A] uppercase tracking-widest mb-4">Engagement Model</p>
              <p className="text-[#f5f5f5] font-bold mb-2">Selective. Senior. Outcome-led.</p>
              <p className="text-[#888888] text-sm leading-relaxed">
                We accept a limited number of mandates each quarter. Every engagement
                receives senior attention — not delegated to a junior team after the
                sales process. Initial engagements begin at $7,500.
                Advisory retainer hours from $10,000 (4-hour block).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <h2 className="text-2xl font-bold mb-4 tracking-tight">Work with us</h2>
            <p className="text-[#888888] mb-8 leading-relaxed">
              Advisory access begins with an application. We review each submission personally
              and respond within 2 business days if there is a clear potential fit.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/apply"
                className="inline-block bg-[#E8651A] text-[#0a0a0a] px-8 py-4 text-sm font-semibold hover:bg-[#C9520E] transition-colors"
              >
                Apply for Advisory Access
              </Link>
              <Link
                to="/founder"
                className="inline-block border border-[#222222] text-[#888888] px-8 py-4 text-sm font-semibold hover:border-[#444444] hover:text-[#f5f5f5] transition-colors"
              >
                About Douglas Bothma
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start gap-8">
          <div>
            <p className="text-[#f5f5f5] font-semibold mb-1">Custom Group · Ballito, South Africa</p>
            <p className="text-xs text-[#888888]">© 2026 Custom Group. All rights reserved.</p>
          </div>
          <div className="flex flex-wrap gap-8 text-sm text-[#888888]">
            <Link to="/blog" className="hover:text-[#f5f5f5] transition-colors">Insights</Link>
            <Link to="/founder" className="hover:text-[#f5f5f5] transition-colors">About</Link>
            <Link to="/apply" className="hover:text-[#f5f5f5] transition-colors">Apply</Link>
            <a
              href="https://www.linkedin.com/in/douglasbothma/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#f5f5f5] transition-colors"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};
